import { requestWsTicket, sessionIdentityEpoch } from '../../../../shared/service/sessionProtocol';
import { readStoredAccessToken } from '../../../../shared/service/requestCredential';
import { useEffect, useRef, useCallback } from 'react';
import { getWebSocketBaseUrl } from '../../../../../config/api';
import { isBrowserOnline } from '../../../../../hooks/useOnlineStatus';
import { useIsActiveView } from '../../../../shared/routing/ViewActivityContext';

export interface MonitoringEnvelope {
  type: 'MONITORING';
  company_id: string;
  ts: string;
  payload: {
    event_type: string;
    message?: string;
    meta?: any;
  };
}

interface UseMonitoringWebSocketOptions {
  /** Evita assinar eventos/abrir conexão para modais fechados. Default mantém os consumidores atuais. */
  enabled?: boolean;
  /**
   * Callback legado: usado para disparar refresh/polling ao receber qualquer MONITORING.
   * Mantido para compatibilidade com as telas atuais.
   */
  onUpdate?: () => void;
  /**
   * Callback novo: recebe o envelope parseado (payload/meta) sem precisar refetch.
   */
  onMessage?: (message: MonitoringEnvelope) => void;
}

const CLOSE_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 15000;

// ✅ Singleton para garantir apenas uma conexão de monitoramento
let globalMonitoringWS: WebSocket | null = null;
let globalPingInterval: ReturnType<typeof setInterval> | null = null;
let globalUpdateListeners: Set<() => void> = new Set();
let globalMessageListeners: Set<(message: MonitoringEnvelope) => void> = new Set();
let closeScheduledTimeout: ReturnType<typeof setTimeout> | null = null;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let pendingCloseNoListeners = false;
let networkListenersInstalled = false;
let shouldReconnect = true;
// Tentativa de conexão em voo (pedindo ticket). O socket só existe depois do ticket:
// sem isto, cada hook montado no mesmo commit pedia o seu.
let connectAttempt = 0;
let connectInFlight = false;

function hasNoListeners(): boolean {
  return globalUpdateListeners.size === 0 && globalMessageListeners.size === 0;
}

function clearPingInterval(): void {
  if (globalPingInterval) {
    clearInterval(globalPingInterval);
    globalPingInterval = null;
  }
}

function clearReconnectTimeout(): void {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
}

/** @internal Limpa estado global — apenas para testes */
export function resetMonitoringWebSocketStateForTests(): void {
  cancelScheduledClose();
  globalUpdateListeners.clear();
  globalMessageListeners.clear();
  pendingCloseNoListeners = false;
  shouldReconnect = true;
  reconnectAttempt = 0;
  clearReconnectTimeout();
  connectAttempt += 1;
  connectInFlight = false;
  if (globalMonitoringWS) {
    try {
      globalMonitoringWS.onopen = null;
      globalMonitoringWS.onmessage = null;
      globalMonitoringWS.onerror = null;
      globalMonitoringWS.onclose = null;
      if (globalMonitoringWS.readyState === WebSocket.OPEN) {
        globalMonitoringWS.close(1000, 'test reset');
      }
    } catch {
      // ignore
    }
    globalMonitoringWS = null;
  }
  clearPingInterval();
}

function cancelScheduledClose(): void {
  if (closeScheduledTimeout) {
    clearTimeout(closeScheduledTimeout);
    closeScheduledTimeout = null;
  }
  pendingCloseNoListeners = false;
}

function performCloseWebSocket(ws: WebSocket, reason: string): void {
  try {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (ws.readyState === WebSocket.OPEN) {
      ws.close(1000, reason);
    }
  } catch (error) {
    console.warn('Erro ao fechar WebSocket de monitoramento:', error);
  } finally {
    if (globalMonitoringWS === ws) {
      globalMonitoringWS = null;
    }
    clearPingInterval();
  }
}

/**
 * Fecha ou abandona um socket sem chamar close() em CONNECTING (evita log do browser com token na URL).
 */
function safeCloseWebSocket(ws: WebSocket, reason: string): void {
  if (ws.readyState === WebSocket.CONNECTING) {
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    ws.onopen = () => {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.close(1000, reason);
        } catch {
          // ignore
        }
      }
    };
    return;
  }

  performCloseWebSocket(ws, reason);
}

function executeScheduledClose(): void {
  closeScheduledTimeout = null;

  if (!hasNoListeners() || !globalMonitoringWS) {
    pendingCloseNoListeners = false;
    return;
  }

  const ws = globalMonitoringWS;
  pendingCloseNoListeners = true;

  if (ws.readyState === WebSocket.CONNECTING) {
    const previousOnOpen = ws.onopen;
    const previousOnError = ws.onerror;
    const previousOnClose = ws.onclose;

    ws.onopen = (event: Event) => {
      previousOnOpen?.call(ws, event);
      if (!pendingCloseNoListeners || !hasNoListeners() || globalMonitoringWS !== ws) {
        pendingCloseNoListeners = false;
        return;
      }
      performCloseWebSocket(ws, 'Sem mais listeners');
      pendingCloseNoListeners = false;
    };

    ws.onerror = (event: Event) => {
      previousOnError?.call(ws, event);
      if (globalMonitoringWS === ws) {
        globalMonitoringWS = null;
      }
      clearPingInterval();
      pendingCloseNoListeners = false;
    };

    ws.onclose = (event: CloseEvent) => {
      previousOnClose?.call(ws, event);
      if (globalMonitoringWS === ws) {
        globalMonitoringWS = null;
      }
      clearPingInterval();
      pendingCloseNoListeners = false;
    };

    return;
  }

  pendingCloseNoListeners = false;
  performCloseWebSocket(ws, 'Sem mais listeners');
}

function scheduleCloseIfNoListeners(): void {
  if (!hasNoListeners() || !globalMonitoringWS) {
    return;
  }

  cancelScheduledClose();

  closeScheduledTimeout = setTimeout(() => {
    executeScheduledClose();
  }, CLOSE_DELAY_MS);
}

function registerListeners(
  onUpdate: (() => void) | undefined,
  onMessage: ((message: MonitoringEnvelope) => void) | undefined,
  stableUpdateListener: () => void,
  stableMessageListener: (msg: MonitoringEnvelope) => void
): void {
  cancelScheduledClose();
  if (onUpdate) globalUpdateListeners.add(stableUpdateListener);
  if (onMessage) globalMessageListeners.add(stableMessageListener);
}

function unregisterListeners(
  stableUpdateListener: () => void,
  stableMessageListener: (msg: MonitoringEnvelope) => void
): void {
  globalUpdateListeners.delete(stableUpdateListener);
  globalMessageListeners.delete(stableMessageListener);

  if (hasNoListeners() && globalMonitoringWS) {
    shouldReconnect = false;
    clearReconnectTimeout();
    scheduleCloseIfNoListeners();
  }
}

function scheduleReconnect(): void {
  if (reconnectTimeout || !shouldReconnect || !isBrowserOnline()) {
    return;
  }

  if (!readStoredAccessToken()) {
    return;
  }

  reconnectAttempt += 1;
  const delay =
    reconnectAttempt === 1
      ? 0
      : Math.min(MAX_RECONNECT_DELAY_MS, 1000 * Math.pow(2, reconnectAttempt - 2));

  if (delay === 0) {
    reconnectMonitoringWebSocket({ resetAttempt: false, reason: 'Reconnect imediato' });
    return;
  }

  reconnectTimeout = setTimeout(() => {
    reconnectTimeout = null;
    reconnectMonitoringWebSocket({ resetAttempt: false, reason: 'Reconnect agendado' });
  }, delay);
}

function installMonitoringNetworkListeners(): void {
  if (networkListenersInstalled || typeof window === 'undefined') return;
  networkListenersInstalled = true;

  window.addEventListener('offline', () => {
    shouldReconnect = false;
    clearReconnectTimeout();
    if (globalMonitoringWS) {
      safeCloseWebSocket(globalMonitoringWS, 'Browser offline');
      if (globalMonitoringWS) {
        globalMonitoringWS = null;
      }
    }
    clearPingInterval();
    cancelScheduledClose();
  });

  window.addEventListener('online', () => {
    if (hasNoListeners()) return;
    if (!readStoredAccessToken()) return;
    shouldReconnect = true;
    reconnectAttempt = 0;
    if (
      globalMonitoringWS &&
      (globalMonitoringWS.readyState === WebSocket.OPEN ||
        globalMonitoringWS.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    connectMonitoringWebSocket();
  });
}

/**
 * Ticket efêmero de uso único, vinculado à sessão e ao endpoint.
 */
async function connectMonitoringWebSocket(): Promise<void> {
  if (!isBrowserOnline()) {
    return;
  }

  const token = readStoredAccessToken();
  if (!token) {
    return;
  }

  if (
    globalMonitoringWS &&
    (globalMonitoringWS.readyState === WebSocket.OPEN ||
      globalMonitoringWS.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }

  if (connectInFlight) {
    return;
  }

  if (globalMonitoringWS) {
    safeCloseWebSocket(globalMonitoringWS, 'Nova conexão sendo criada');
    globalMonitoringWS = null;
    clearPingInterval();
  }

  const baseUrl = getWebSocketBaseUrl();
  const identityEpoch = sessionIdentityEpoch();
  const attempt = ++connectAttempt;
  connectInFlight = true;
  let ticket: string;
  try { ticket = await requestWsTicket('/api/v1/ws/monitoring'); }
  catch {
    if (attempt !== connectAttempt) return;
    connectInFlight = false;
    scheduleReconnect();
    return;
  }
  if (attempt !== connectAttempt) return; // superada por uma reconexão explícita
  connectInFlight = false;
  if (identityEpoch !== sessionIdentityEpoch() || !readStoredAccessToken() || hasNoListeners() || globalMonitoringWS) return;
  const wsUrl = `${baseUrl}/api/v1/ws/monitoring?ticket=${encodeURIComponent(ticket)}`;

  const ws = new WebSocket(wsUrl);
  globalMonitoringWS = ws;

  ws.onopen = () => {
    clearReconnectTimeout();
    reconnectAttempt = 0;
    if (pendingCloseNoListeners && hasNoListeners() && globalMonitoringWS === ws) {
      performCloseWebSocket(ws, 'Sem mais listeners');
      pendingCloseNoListeners = false;
      return;
    }

    globalPingInterval = setInterval(() => {
      if (globalMonitoringWS && globalMonitoringWS.readyState === WebSocket.OPEN) {
        globalMonitoringWS.send('ping');
      }
    }, 30000);
  };

  ws.onmessage = (event) => {
    if (event.data === 'pong' || event.data === 'PONG') {
      return;
    }

    try {
      const message: MonitoringEnvelope = JSON.parse(event.data);

      if (message.type === 'MONITORING') {
        globalMessageListeners.forEach((listener) => {
          try {
            listener(message);
          } catch (err) {
            console.warn('Erro ao executar listener de mensagem de monitoramento:', err);
          }
        });

        globalUpdateListeners.forEach((listener) => {
          try {
            listener();
          } catch (err) {
            console.warn('Erro ao executar listener de monitoramento:', err);
          }
        });
      }
    } catch {
      // Erro silencioso ao processar mensagem
    }
  };

  ws.onerror = () => {
    // Silencioso — evita expor URL/token no console do browser
  };

  ws.onclose = () => {
    clearPingInterval();
    if (globalMonitoringWS === ws) {
      globalMonitoringWS = null;
    }
    pendingCloseNoListeners = false;

    if (!shouldReconnect) {
      return;
    }

    scheduleReconnect();
  };
}

export function reconnectMonitoringWebSocket(options?: {
  resetAttempt?: boolean;
  reason?: string;
}): void {
  if (hasNoListeners()) {
    return;
  }

  const resetAttempt = options?.resetAttempt ?? true;
  const closeReason = options?.reason ?? 'Token atualizado';

  shouldReconnect = true;
  cancelScheduledClose();
  clearReconnectTimeout();
  if (resetAttempt) {
    reconnectAttempt = 0;
  }

  if (globalMonitoringWS) {
    safeCloseWebSocket(globalMonitoringWS, closeReason);
    globalMonitoringWS = null;
    clearPingInterval();
  }

  // Reconexão explícita (ex.: identidade nova) não reaproveita o ticket em voo.
  connectAttempt += 1;
  connectInFlight = false;
  connectMonitoringWebSocket();
}

/**
 * Hook para gerenciar conexão WebSocket de monitoramento
 * Conecta ao entrar na tela e desconecta ao sair
 */
export const useMonitoringWebSocket = ({ onUpdate, onMessage, enabled = true }: UseMonitoringWebSocketOptions) => {
  const onUpdateRef = useRef(onUpdate);
  const onMessageRef = useRef(onMessage);
  const isMountedRef = useRef(true);
  const isActiveView = useIsActiveView();
  const isPaneActiveRef = useRef(isActiveView);
  isPaneActiveRef.current = isActiveView;

  const stableUpdateListener = useCallback(() => {
    if (!isPaneActiveRef.current) return;
    if (isMountedRef.current && onUpdateRef.current) {
      onUpdateRef.current();
    }
  }, []);

  const stableMessageListener = useCallback((msg: MonitoringEnvelope) => {
    const eventType = msg?.type === 'MONITORING' ? msg.payload?.event_type : undefined;
    const isCriticalEnvelope =
      eventType === 'TEMPO_ACEITACAO' || eventType === 'MAQUINA_STATUS';
    if (!isPaneActiveRef.current && !isCriticalEnvelope) return;
    if (isMountedRef.current && onMessageRef.current) {
      onMessageRef.current(msg);
    }
  }, []);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
    onMessageRef.current = onMessage;
  }, [onUpdate, onMessage]);

  useEffect(() => {
    if (!enabled) return;
    installMonitoringNetworkListeners();
    isMountedRef.current = true;
    shouldReconnect = true;

    const token = readStoredAccessToken();
    if (!token) {
      return;
    }

    if (
      globalMonitoringWS &&
      (globalMonitoringWS.readyState === WebSocket.OPEN ||
        globalMonitoringWS.readyState === WebSocket.CONNECTING)
    ) {
      registerListeners(onUpdate, onMessage, stableUpdateListener, stableMessageListener);

      return () => {
        isMountedRef.current = false;
        unregisterListeners(stableUpdateListener, stableMessageListener);
      };
    }

    connectMonitoringWebSocket();
    registerListeners(onUpdate, onMessage, stableUpdateListener, stableMessageListener);

    return () => {
      isMountedRef.current = false;
      unregisterListeners(stableUpdateListener, stableMessageListener);
    };
  }, [enabled, onMessage, onUpdate, stableMessageListener, stableUpdateListener]);
};
